// @tg-seq: 11201
// @tg-api-binding: 11201-1
// @architect-approved: HARDCODED_COLORS - Modal backdrop dim overlay, decorative
// chrome, not themeable UI.
// @tg-golden-rule: BEFORE debugging, validate inside-out (Layer 1-7).
// Read: docs/agent-contracts/AGENT_LEGO_BLOCK_GOLDEN_RULE.md

/**
 * Universal Contact Search — reverse "LinkedIn-style" contact resolver
 *
 * Start at any level (org / hierarchy / department / dept group / role) and fan out
 * to find the contact(s) currently assigned. Shows chat opt-in + last-login presence
 * hints, and launches Chat / Phone / Email directly from a result row.
 *
 * Backend: apiRequestBySeq(11201, 1, ...) -> /api/tsx/11201/1 -> ORG.sp_Universal_Contact_Search_Controller
 * Manage: 5 = Search, 6 = Facets (cascading OrgMgtHierarchy/Department/DeptGroup/OrgRole options)
 *
 * Phase 1 scope: same-organization search only (org-to-org opt-in directory is Phase 2/3).
 * See: /memories/repo/universal-contact-search-virtual-directory-design.md
 */

// === AUTO-GENERATED START: API_ENDPOINTS ===
/**
 * API Endpoint Registry - Component 11201
 * Generated from Router_Registry (TSX_Seq_Num = 11201)
 * Auto-synced on build - DO NOT EDIT MANUALLY
 *
 * Router 1: tsx_11201_1.py (null)
 *   Controller: ORG.sp_Universal_Contact_Search_Controller
 *   Endpoint: /api/tsx/11201/1
 *
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const API_ENDPOINTS = {
    MAIN: '/api/tsx/11201/1',
};
// === AUTO-GENERATED END: API_ENDPOINTS ===

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    MenuItem,
    Chip,
    Paper,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    InputAdornment,
} from '@mui/material';
import { DataGrid, GridColDef, GridRowsProp } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import ChatIcon from '@mui/icons-material/Chat';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import CircleIcon from '@mui/icons-material/Circle';
import { useAuth } from '../../utils/useAuth';
import { apiRequestBySeq } from '../../utils/apiService';
import { getTsxFileName } from '../../utils/componentIdentity';
import ChatPanel from './scr_tgc_10639_chat_panel';
import PhoneTool from './scr_tgc_10989_phone_tool';
import MallDirectoryCommandCenter from './scr_tgc_10797_mall_directory_command_center';

interface ContactRow {
    org_pfuser_association_uuid: string;
    organization_uuid: string;
    organization_name: string;
    pfuser_uuid: string;
    display_name: string;
    full_name: string;
    contact_email: string;
    primary_phone: string;
    show_online_status: boolean;
    lastlogin_date: string | null;
    user_is_active: boolean;
    primary_orgrole_id: number | null;
    active_status_id: number | null;
    orgchat_optin_status_id: number | null;
    orgmgthierarchy_id: number | null;
    orgmgthierarchy: string | null;
    role_department_id: number | null;
    role_department: string | null;
    deptgroup_id: number | null;
    deptgroup: string | null;
}

interface FacetOption {
    orgmgthierarchy_id: number | null;
    orgmgthierarchy: string | null;
    role_department_id: number | null;
    role_department: string | null;
    deptgroup_id: number | null;
    deptgroup: string | null;
    primary_orgrole_id: number | null;
}

const ALL = -1;

const Self = () => {
    const { activeOrganizationUUID } = useAuth();

    const [searchText, setSearchText] = useState('');
    const [hierarchyId, setHierarchyId] = useState<number>(ALL);
    const [departmentId, setDepartmentId] = useState<number>(ALL);
    const [deptgroupId, setDeptgroupId] = useState<number>(ALL);
    const [onlyActive, setOnlyActive] = useState(true);

    const [facets, setFacets] = useState<FacetOption[]>([]);
    const [rows, setRows] = useState<ContactRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [chatTarget, setChatTarget] = useState<string | null>(null);
    const [phoneOpen, setPhoneOpen] = useState(false);
    const [orgDirectoryOpen, setOrgDirectoryOpen] = useState(false);

    // Load cascading facet options for this org
    // 'ALL' is a Redux UI sentinel for "All Organizations" selected - NOT a real GUID.
    // Sending it straight through to @organization_uuid (UNIQUEIDENTIFIER) throws
    // "Error converting data type varchar to uniqueidentifier" -> 400 (HF-2026-09-08-003).
    const orgUuidParam =
        activeOrganizationUUID && activeOrganizationUUID !== 'ALL' ? activeOrganizationUUID : null;

    useEffect(() => {
        if (!orgUuidParam) return;
        apiRequestBySeq<FacetOption[]>(11201, 1, {
            manage: 6,
            OrganizationUuid: orgUuidParam,
        })
            .then((data) => setFacets(data || []))
            .catch(() => setFacets([]));
    }, [orgUuidParam]);

    const runSearch = useCallback(() => {
        if (!orgUuidParam) return;
        setLoading(true);
        setError(null);
        apiRequestBySeq<ContactRow[]>(11201, 1, {
            manage: 5,
            OrganizationUuid: orgUuidParam,
            SearchText: searchText || null,
            OrgmgthierarchyId: hierarchyId === ALL ? null : hierarchyId,
            DepartmentId: departmentId === ALL ? null : departmentId,
            DeptgroupId: deptgroupId === ALL ? null : deptgroupId,
            OnlyActive: onlyActive,
        })
            .then((data) => setRows(data || []))
            .catch((e) => setError(e?.message || 'Search failed'))
            .finally(() => setLoading(false));
    }, [orgUuidParam, searchText, hierarchyId, departmentId, deptgroupId, onlyActive]);

    useEffect(() => {
        runSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeOrganizationUUID, hierarchyId, departmentId, deptgroupId, onlyActive]);

    const hierarchyOptions = useMemo(() => {
        const seen = new Map<number, string>();
        facets.forEach((f) => {
            if (f.orgmgthierarchy_id != null)
                seen.set(f.orgmgthierarchy_id, f.orgmgthierarchy || String(f.orgmgthierarchy_id));
        });
        return Array.from(seen.entries());
    }, [facets]);

    const departmentOptions = useMemo(() => {
        const seen = new Map<number, string>();
        facets
            .filter((f) => hierarchyId === ALL || f.orgmgthierarchy_id === hierarchyId)
            .forEach((f) => {
                if (f.role_department_id != null)
                    seen.set(
                        f.role_department_id,
                        f.role_department || String(f.role_department_id),
                    );
            });
        return Array.from(seen.entries());
    }, [facets, hierarchyId]);

    const deptgroupOptions = useMemo(() => {
        const seen = new Map<number, string>();
        facets
            .filter(
                (f) =>
                    (hierarchyId === ALL || f.orgmgthierarchy_id === hierarchyId) &&
                    (departmentId === ALL || f.role_department_id === departmentId),
            )
            .forEach((f) => {
                if (f.deptgroup_id != null)
                    seen.set(f.deptgroup_id, f.deptgroup || String(f.deptgroup_id));
            });
        return Array.from(seen.entries());
    }, [facets, hierarchyId, departmentId]);

    const columns: GridColDef[] = [
        {
            field: 'display_name',
            headerName: 'Contact',
            width: 200,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title={params.row.user_is_active ? 'Active user' : 'Inactive user'}>
                        <CircleIcon
                            sx={{
                                fontSize: 10,
                                color: params.row.user_is_active ? 'success.main' : 'text.disabled',
                            }}
                        />
                    </Tooltip>
                    <Typography variant="body2">{params.value || params.row.full_name}</Typography>
                </Box>
            ),
        },
        { field: 'organization_name', headerName: 'Organization', width: 180 },
        { field: 'orgmgthierarchy', headerName: 'Hierarchy', width: 130 },
        { field: 'role_department', headerName: 'Department', width: 140 },
        { field: 'deptgroup', headerName: 'Dept Group', width: 150 },
        {
            field: 'orgchat_optin_status_id',
            headerName: 'Chat',
            width: 90,
            renderCell: (params) =>
                params.value ? (
                    <Chip size="small" color="success" label="Opted-in" />
                ) : (
                    <Chip size="small" variant="outlined" label="Opted-out" />
                ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 140,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Chat">
                        <span>
                            <IconButton
                                size="small"
                                disabled={!params.row.orgchat_optin_status_id}
                                onClick={() => setChatTarget(params.row.pfuser_uuid)}
                            >
                                <ChatIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Call">
                        <span>
                            <IconButton
                                size="small"
                                disabled={!params.row.primary_phone}
                                onClick={() => setPhoneOpen(true)}
                            >
                                <PhoneIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Email">
                        <span>
                            <IconButton
                                size="small"
                                disabled={!params.row.contact_email}
                                onClick={() =>
                                    (window.location.href = `mailto:${params.row.contact_email}`)
                                }
                            >
                                <EmailIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    const gridRows: GridRowsProp = rows.map((r) => ({ id: r.org_pfuser_association_uuid, ...r }));

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1 }}>
            <Typography variant="h6">Universal Contact Search</Typography>
            <Typography
                variant="body2"
                sx={{
                    color: 'text.secondary',
                }}
            >
                Start anywhere — search by name, or drill from Hierarchy → Department → Dept Group
                to find who owns a role.
            </Typography>

            <Paper
                variant="outlined"
                sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}
            >
                <TextField
                    size="small"
                    label="Search name / org / role"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                    sx={{ minWidth: 240 }}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={runSearch}>
                                        <SearchIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />
                <TextField
                    select
                    size="small"
                    label="Hierarchy"
                    value={hierarchyId}
                    onChange={(e) => {
                        setHierarchyId(Number(e.target.value));
                        setDepartmentId(ALL);
                        setDeptgroupId(ALL);
                    }}
                    sx={{ minWidth: 160 }}
                >
                    <MenuItem value={ALL}>All</MenuItem>
                    {hierarchyOptions.map(([id, label]) => (
                        <MenuItem key={id} value={id}>
                            {label}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    size="small"
                    label="Department"
                    value={departmentId}
                    onChange={(e) => {
                        setDepartmentId(Number(e.target.value));
                        setDeptgroupId(ALL);
                    }}
                    sx={{ minWidth: 160 }}
                >
                    <MenuItem value={ALL}>All</MenuItem>
                    {departmentOptions.map(([id, label]) => (
                        <MenuItem key={id} value={id}>
                            {label}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    size="small"
                    label="Dept Group"
                    value={deptgroupId}
                    onChange={(e) => setDeptgroupId(Number(e.target.value))}
                    sx={{ minWidth: 160 }}
                >
                    <MenuItem value={ALL}>All</MenuItem>
                    {deptgroupOptions.map(([id, label]) => (
                        <MenuItem key={id} value={id}>
                            {label}
                        </MenuItem>
                    ))}
                </TextField>
                <Chip
                    label={onlyActive ? 'Active only' : 'All statuses'}
                    color={onlyActive ? 'primary' : 'default'}
                    onClick={() => setOnlyActive((v) => !v)}
                    variant={onlyActive ? 'filled' : 'outlined'}
                />
                <Chip
                    label="Org Directory View"
                    color="secondary"
                    variant="outlined"
                    onClick={() => setOrgDirectoryOpen(true)}
                />
            </Paper>

            {error && <Alert severity="error">{error}</Alert>}

            <Box sx={{ position: 'relative' }}>
                <DataGrid
                    rows={gridRows}
                    columns={columns}
                    loading={loading}
                    sx={{ height: 520, width: '100%', bgcolor: 'background.paper' }}
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                    pageSizeOptions={[25, 50, 100]}
                />
                {loading && (
                    <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                        <CircularProgress size={18} />
                    </Box>
                )}
            </Box>

            <ChatPanel
                open={!!chatTarget}
                onClose={() => setChatTarget(null)}
                filterContext={{ pfuser_uuid: chatTarget || undefined }}
            />
            <PhoneTool open={phoneOpen} onClose={() => setPhoneOpen(false)} />

            {/* Org Directory View overlay (seq 10797 - mall_directory_command_center) -
                self-contained component, no props; rendered as a full-screen overlay on
                top of this dialog rather than navigating away from the search. */}
            {orgDirectoryOpen && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(0,0,0,0.5)',
                        zIndex: 1300,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2,
                    }}
                    onClick={() => setOrgDirectoryOpen(false)}
                >
                    <Box
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                            bgcolor: 'background.default',
                            borderRadius: 1,
                            maxWidth: 'xl',
                            width: '100%',
                            maxHeight: '90vh',
                            overflow: 'auto',
                        }}
                    >
                        <MallDirectoryCommandCenter />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

Self.displayName = getTsxFileName(import.meta.url);

export default Self;
